import React, {
  useMemo, useCallback, useState, useEffect, useRef, useLayoutEffect,
} from 'react'
import { List, Map as ImmutableMap, fromJS } from 'immutable'
import Row, { STYLES } from '../../../../Row.v2/index'
import TileV2Graphql from '../../../../Tile.v2/Tile.v2Graphql/index'
import Button, { TYPES as BUTTON_TYPES } from '../../../../Button/index'
import ChevronDown from '../../../../Icon/icons/ChevronDown/index'
import ChevronUp from '../../../../Icon/icons/ChevronUp/index'
import useIsMobile from '../../../../../services/community/shared/useIsMobile'
import { PlaylistEditToolbar } from '../PlaylistEditToolbar/index'
import type { EnrichedPlaylistItem, EnrichedInProgressItem } from '../../../../../services/community/types'
import TileV2Skeleton from './TileV2Skeleton'
import PlaylistItemCarouselSkeleton from './PlaylistItemCarouselSkeleton'

const NO_ACCESSOR = () => null

/** Accept either playlist items or in-progress items to avoid unsafe double-casts. */
export type CarouselItem = EnrichedPlaylistItem | EnrichedInProgressItem

/**
 * Reads the pre-converted Immutable Map from enrichment (tileDataImmutable).
 * Falls back to fromJS() for plain objects (test data, legacy callers).
 */
function getImmutableTileData (item: CarouselItem): ImmutableMap<string, unknown> | null {
  if (item.tileDataImmutable) return item.tileDataImmutable
  if (!item.tileData) return null
  if (ImmutableMap.isMap(item.tileData)) return item.tileData as ImmutableMap<string, unknown>
  return fromJS(item.tileData) as ImmutableMap<string, unknown>
}

// ---------------------------------------------------------------------------
// Reorder callback type — shared by both carousel and grid.  When provided
// alongside `editMode`, each tile renders <PlaylistReorderControls> so the
// user can move items one slot earlier / later via real <button> clicks.
// Signature mirrors the prior DnD primitive (`fromIndex, toIndex, itemId`)
// so existing reorder handlers keep working unchanged.
// ---------------------------------------------------------------------------
export type OnReorderItem = (
  fromIndex: number,
  toIndex: number,
  draggedItemId: string,
) => void

/**
 * Localized aria-label / tooltip strings for the four edit-toolbar pills.
 * Threaded through ResponsivePlaylistDisplay → PlaylistTileGrid /
 * PlaylistItemCarousel → PlaylistEditToolbar so callers can resolve them
 * from their own staticText bundle (profileMyPlaylists for the Library tab,
 * communityMyLibraryPage for the System full-view).
 */
export interface EditToolbarLabels {
  earlierLabel?: string
  laterLabel?: string
  removeLabel?: string
  confirmLabel?: string
}

export interface PlaylistItemCarouselProps {
  items: CarouselItem[]
  editMode?: boolean
  onRemoveItem?: (item: CarouselItem) => void | Promise<void>
  /** Required for reorder controls — the controls are hidden if omitted. */
  onReorderItem?: OnReorderItem
  /** Toolbar pill + icon size — small (default), medium, or large. */
  editToolbarSize?: 'small' | 'medium' | 'large'
  /** When true, every reorder chevron in the row is disabled.  Used by the
   *  parent to lock clicks while a move mutation is in flight (prevents
   *  rapid-click queueing that would race against stale positions). */
  reorderDisabled?: boolean
  /** i18n labels for the edit toolbar pills. */
  editToolbarLabels?: EditToolbarLabels
}

function PlaylistItemCarouselBase ({
  items,
  editMode = false,
  onRemoveItem,
  onReorderItem,
  editToolbarSize = 'small',
  reorderDisabled = false,
  editToolbarLabels,
}: PlaylistItemCarouselProps) {
  // Pair each source item with its tile data and filter together, so the
  // Row's index always maps back to the correct source item.  Indexing the
  // unfiltered `items` by the filtered Row index removes/reorders the wrong
  // video whenever an item has no resolvable tile data.
  const tilePairs = useMemo(
    () => items
      .map((sourceItem) => ({ sourceItem, td: getImmutableTileData(sourceItem) }))
      .filter(
        (p): p is { sourceItem: CarouselItem; td: ImmutableMap<string, unknown> } => !!p.td,
      ),
    [items],
  )
  const tileItems = useMemo(() => List(tilePairs.map((p) => p.td)), [tilePairs])

  // Track in-flight delete IDs so we can render a `<TileV2Placeholder>` ghost
  // in the same grid slot until the parent's mutation+refetch resolves.
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string>>(() => new Set())

  // Drop pending IDs once they've vanished from the upstream items array.
  // We rely on this rather than clearing in a `finally` block because
  // `await refetchPlaylists()` resolves BEFORE React commits the post-refetch
  // render — clearing then would briefly show the original tile again before
  // the items array updates.
  useEffect(() => {
    if (pendingDeleteIds.size === 0) return
    const presentIds = new Set(items.map((it) => it.id))
    let dirty = false
    const next = new Set<string>()
    pendingDeleteIds.forEach((id) => {
      if (presentIds.has(id)) next.add(id)
      else dirty = true
    })
    if (dirty) setPendingDeleteIds(next)
  }, [items, pendingDeleteIds])

  const handleRemoveWithGhost = useCallback((item: CarouselItem) => {
    if (!onRemoveItem) return
    setPendingDeleteIds((prev) => {
      const next = new Set(prev)
      next.add(item.id)
      return next
    })
    // Fire and let the effect above clear the ghost when the item disappears
    // from `items`.  On failure we clear the ghost ourselves so the user can
    // retry; the catch is intentionally swallowed because the parent handler
    // already surfaces errors via toast.
    Promise.resolve(onRemoveItem(item)).catch(() => {
      setPendingDeleteIds((prev) => {
        if (!prev.has(item.id)) return prev
        const next = new Set(prev)
        next.delete(item.id)
        return next
      })
    })
  }, [onRemoveItem])

  // Row.v2 reads its container offsetWidth once at mount to derive how many
  // tiles fit (`limit`) and only re-measures on items/viewportWidth change.
  // While a row is off-screen, `content-visibility: auto` on the playlist row
  // skips its subtree layout → width 0 → limit 0 → every tile is
  // visibility:hidden while the chevron still paints. Gate the <Row> mount on
  // a real width; a lifetime ResizeObserver remounts it (via key) when the row
  // is revealed at a width that changed while it was hidden, so Row.v2
  // re-measures. On-screen resizes are left to Row.v2's own reconfigure.
  const containerRef = useRef<HTMLDivElement>(null)
  const [mountKey, setMountKey] = useState(0)
  const mountedRef = useRef(false)
  const mountedWidthRef = useRef(0)
  const wasHiddenRef = useRef(false)

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return undefined
    const mount = (width: number) => {
      mountedRef.current = true
      mountedWidthRef.current = width
      wasHiddenRef.current = false
      setMountKey((k) => k + 1)
    }
    // Visible mount: width is already real → mount before paint (no flash).
    if (el.offsetWidth > 0) {
      mount(el.offsetWidth)
      return undefined
    }
    // SSR / Firefox (no content-visibility) / jsdom: fail open so the carousel
    // always renders rather than getting stuck on the skeleton.
    if (typeof ResizeObserver === 'undefined') {
      mount(el.offsetWidth)
      return undefined
    }
    const ro = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0
      if (width <= 0) {
        // Row scrolled off-screen (content-visibility skipped the subtree).
        if (mountedRef.current) wasHiddenRef.current = true
        return
      }
      if (!mountedRef.current) {
        mount(width) // first reveal
      } else if (wasHiddenRef.current) {
        // Revealed after being hidden: remount only if the width changed while
        // hidden (a resize Row.v2 couldn't have measured at 0 width).
        if (width !== mountedWidthRef.current) mount(width)
        else wasHiddenRef.current = false
      } else {
        // On-screen resize — Row.v2 reconfigures itself; just track the width.
        mountedWidthRef.current = width
      }
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  if (!tileItems.size) return null

  return (
    <div ref={containerRef} className="playlist-item-carousel-measure">
      {mountKey > 0 ? (
        <Row
          key={mountKey}
          items={tileItems}
          style={STYLES.S12345}
          disableScale
          createAccessor={NO_ACCESSOR}
        >
      {(item: ImmutableMap<string, unknown>, params: { index: number }) => {
        const pair = tilePairs[params.index]
        const sourceItem = pair?.sourceItem
        const isPendingDelete = sourceItem ? pendingDeleteIds.has(sourceItem.id) : false
        const tile = (
          <TileV2Graphql
            data={item}
            contentType={item.get('contentType', 'video')}
            index={params.index}
          />
        )
        if (!editMode) return tile
        const wrapperClassName = isPendingDelete
          ? 'playlist-tile-grid__edit-wrapper playlist-tile-grid__edit-wrapper--pending-delete'
          : 'playlist-tile-grid__edit-wrapper'
        return (
          <div className={wrapperClassName}>
            {/* Always keep the real tile mounted so the cell preserves its
                full height — swapping in a shorter <TileV2Placeholder>
                triggers a row reflow at <768px (2-col grid).  When a delete
                is pending, an absolutely-positioned overlay mounts a
                <TileV2Skeleton> matching the initial-load skeleton; the
                wrapper's white background masks the real tile so its
                content doesn't bleed through the skeleton's whitespace. */}
            {tile}
            {isPendingDelete ? (
              <div
                className="playlist-tile-grid__delete-ghost"
                aria-hidden
                aria-busy
              >
                <TileV2Skeleton />
              </div>
            ) : (
              <>
                <div className="playlist-tile-grid__edit-backdrop" />
                {sourceItem ? (
                  <PlaylistEditToolbar
                    index={params.index}
                    totalCount={tilePairs.length}
                    itemId={sourceItem.id}
                    onReorder={onReorderItem}
                    onRemove={
                      onRemoveItem ? () => handleRemoveWithGhost(sourceItem) : undefined
                    }
                    size={editToolbarSize}
                    reorderDisabled={reorderDisabled}
                    earlierLabel={editToolbarLabels?.earlierLabel}
                    laterLabel={editToolbarLabels?.laterLabel}
                    removeLabel={editToolbarLabels?.removeLabel}
                    confirmLabel={editToolbarLabels?.confirmLabel}
                  />
                ) : null}
              </>
            )}
          </div>
        )
      }}
        </Row>
      ) : (
        <PlaylistItemCarouselSkeleton count={tileItems.size} />
      )}
    </div>
  )
}

export const PlaylistItemCarousel = React.memo(PlaylistItemCarouselBase)

export interface PlaylistTileGridProps {
  items: CarouselItem[]
  initialCount?: number
  seeMoreLabel: string
  showLessLabel?: string
  editMode?: boolean
  onRemoveItem?: (item: CarouselItem) => void | Promise<void>
  hideToggle?: boolean
  /** Required for reorder controls — the controls are hidden if omitted. */
  onReorderItem?: OnReorderItem
  /** Toolbar pill + icon size — small (default), medium, or large. */
  editToolbarSize?: 'small' | 'medium' | 'large'
  /** When true, every reorder chevron in the grid is disabled.  See
   *  PlaylistItemCarouselProps for the full rationale. */
  reorderDisabled?: boolean
  /** i18n labels for the edit toolbar pills. */
  editToolbarLabels?: EditToolbarLabels
}

function PlaylistTileGridBase ({
  items,
  initialCount = 8,
  seeMoreLabel,
  showLessLabel = 'Show less',
  editMode = false,
  onRemoveItem,
  hideToggle = false,
  onReorderItem,
  editToolbarSize = 'small',
  reorderDisabled = false,
  editToolbarLabels,
}: PlaylistTileGridProps) {
  const [expanded, setExpanded] = useState(false)
  const handleExpand = useCallback(() => setExpanded(true), [])
  const handleCollapse = useCallback(() => setExpanded(false), [])

  const visibleItems = expanded ? items : items.slice(0, initialCount)

  const tileDataMap = useMemo(() => {
    const map = new Map<string, ImmutableMap<string, unknown> | null>()
    visibleItems.forEach((item) => {
      const key = item.contentId || item.id
      map.set(key, getImmutableTileData(item))
    })
    return map
  }, [visibleItems])

  // Track in-flight delete IDs so we can render a `<TileV2Placeholder>` ghost
  // in the same grid slot until the parent's mutation+refetch resolves.
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string>>(() => new Set())

  // Drop pending IDs once they've vanished from the upstream items array.
  // See PlaylistItemCarouselBase for the rationale (refetch resolves before
  // React commits the post-refetch render).
  useEffect(() => {
    if (pendingDeleteIds.size === 0) return
    const presentIds = new Set(items.map((it) => it.id))
    let dirty = false
    const next = new Set<string>()
    pendingDeleteIds.forEach((id) => {
      if (presentIds.has(id)) next.add(id)
      else dirty = true
    })
    if (dirty) {
      setPendingDeleteIds(next)
    }
  }, [items, pendingDeleteIds])

  const handleRemoveWithGhost = useCallback((item: CarouselItem) => {
    if (!onRemoveItem) return
    setPendingDeleteIds((prev) => {
      const next = new Set(prev)
      next.add(item.id)
      return next
    })
    Promise.resolve(onRemoveItem(item)).catch(() => {
      setPendingDeleteIds((prev) => {
        if (!prev.has(item.id)) return prev
        const next = new Set(prev)
        next.delete(item.id)
        return next
      })
    })
  }, [onRemoveItem, items.length])

  return (
    <div className="playlist-tile-grid">
      <div className="playlist-tile-grid__inner">
        {visibleItems.map((item, index) => {
          const key = item.contentId || item.id
          const tileData = tileDataMap.get(key)
          if (!tileData) return null
          const isPendingDelete = pendingDeleteIds.has(item.id)
          const tile = (
            <TileV2Graphql
              data={tileData}
              contentType={tileData.get('contentType', 'video')}
              index={index}
            />
          )
          const isEditing = editMode && (onRemoveItem || onReorderItem)
          const wrapperClassName = isPendingDelete
            ? 'playlist-tile-grid__edit-wrapper playlist-tile-grid__edit-wrapper--pending-delete'
            : 'playlist-tile-grid__edit-wrapper'
          return (
            <div key={key} className="playlist-tile-grid__item">
              {isEditing ? (
                <div className={wrapperClassName}>
                  {/* Always keep the real tile mounted so the cell preserves
                      its full height — swapping in a shorter placeholder
                      triggers a row reflow at <768px (2-col grid).  When a
                      delete is pending, an absolutely-positioned overlay
                      mounts a <TileV2Skeleton> matching the initial-load
                      skeleton; the wrapper's white background masks the
                      real tile so its content doesn't bleed through the
                      skeleton's whitespace. */}
                  {tile}
                  {isPendingDelete ? (
                    <div
                      className="playlist-tile-grid__delete-ghost"
                      aria-hidden
                      aria-busy
                    >
                      <TileV2Skeleton />
                    </div>
                  ) : (
                    <>
                      <div className="playlist-tile-grid__edit-backdrop" />
                      <PlaylistEditToolbar
                        index={index}
                        totalCount={items.length}
                        itemId={item.id}
                        onReorder={onReorderItem}
                        onRemove={onRemoveItem ? () => handleRemoveWithGhost(item) : undefined}
                        size={editToolbarSize}
                        reorderDisabled={reorderDisabled}
                        earlierLabel={editToolbarLabels?.earlierLabel}
                        laterLabel={editToolbarLabels?.laterLabel}
                        removeLabel={editToolbarLabels?.removeLabel}
                        confirmLabel={editToolbarLabels?.confirmLabel}
                      />
                    </>
                  )}
                </div>
              ) : tile}
            </div>
          )
        })}
      </div>
      {!hideToggle && items.length > initialCount && !expanded ? (
        <Button
          type={BUTTON_TYPES.LINK}
          className="playlist-tile-grid__toggle"
          onClick={handleExpand}
        >
          {seeMoreLabel}
          <ChevronDown />
        </Button>
      ) : null}
      {!hideToggle && expanded && items.length > initialCount ? (
        <Button
          type={BUTTON_TYPES.LINK}
          className="playlist-tile-grid__toggle"
          onClick={handleCollapse}
        >
          {showLessLabel}
          <ChevronUp />
        </Button>
      ) : null}
    </div>
  )
}

export const PlaylistTileGrid = React.memo(PlaylistTileGridBase)

/**
 * Unified responsive wrapper: renders PlaylistTileGrid on mobile,
 * PlaylistItemCarousel on desktop.
 */
export interface ResponsivePlaylistDisplayProps {
  items: CarouselItem[]
  initialCount?: number
  seeMoreLabel: string
  showLessLabel?: string
  editMode?: boolean
  onRemoveItem?: (item: CarouselItem) => void | Promise<void>
  hideToggle?: boolean
  /** Reorder callback — forwarded to whichever child renders. */
  onReorderItem?: OnReorderItem
  /** Toolbar pill + icon size — small (default), medium, or large. */
  editToolbarSize?: 'small' | 'medium' | 'large'
  /** Lock all reorder chevrons.  Forwarded to the underlying grid/carousel. */
  reorderDisabled?: boolean
  /** i18n labels for the edit toolbar pills. */
  editToolbarLabels?: EditToolbarLabels
}

function ResponsivePlaylistDisplayBase ({
  items,
  initialCount = 4,
  seeMoreLabel,
  showLessLabel,
  editMode,
  onRemoveItem,
  hideToggle,
  onReorderItem,
  editToolbarSize = 'small',
  reorderDisabled = false,
  editToolbarLabels,
}: ResponsivePlaylistDisplayProps) {
  const isMobile = useIsMobile()
  if (isMobile) {
    return (
      <PlaylistTileGridBase
        items={items}
        initialCount={initialCount}
        seeMoreLabel={seeMoreLabel}
        showLessLabel={showLessLabel}
        editMode={editMode}
        onRemoveItem={onRemoveItem}
        hideToggle={hideToggle}
        onReorderItem={onReorderItem}
        editToolbarSize={editToolbarSize}
        reorderDisabled={reorderDisabled}
        editToolbarLabels={editToolbarLabels}
      />
    )
  }
  return (
    <PlaylistItemCarouselBase
      items={items}
      editMode={editMode}
      onRemoveItem={onRemoveItem}
      onReorderItem={onReorderItem}
      editToolbarSize={editToolbarSize}
      reorderDisabled={reorderDisabled}
      editToolbarLabels={editToolbarLabels}
    />
  )
}

export const ResponsivePlaylistDisplay = React.memo(ResponsivePlaylistDisplayBase)
